import { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { Btn, Field, ImagesField } from "@/admin/ui";

const EMPTY = { name: "", type: "", label: "", tagline: "", description: "", story: "", price: "", sale_price: "", stock: 0, variant_name: "Size", variants: [], colours: [], materials: "", specs: [], occasions: [], images: [], published: true, featured: false };
const OCC = [["everyday", "Everyday Elegance"], ["festive", "Festive Moments"], ["celebrations", "Special Celebrations"], ["gifting", "Thoughtful Gifting"]];

const Rows = ({ rows, onChange, a, b, addLabel, bType = "text", testId }) => (
  <div className="space-y-2">
    {rows.map((r, i) => (
      <div key={i} className="flex gap-2">
        <input className="rh-input" placeholder={a.ph} value={r[a.k]} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, [a.k]: e.target.value } : x)))} data-testid={`${testId}-${i}-a`} />
        <input className="rh-input" type={bType} placeholder={b.ph} value={r[b.k]} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, [b.k]: bType === "number" ? Number(e.target.value) : e.target.value } : x)))} data-testid={`${testId}-${i}-b`} />
        <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="w-12 flex-none rounded-[12px] text-ruby hover:bg-ruby/10 grid place-items-center" aria-label="Remove"><X className="w-4 h-4" /></button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...rows, { [a.k]: "", [b.k]: bType === "number" ? 0 : "" }])} className="flex items-center gap-1.5 text-[13px] font-bold text-berry" data-testid={`${testId}-add`}><Plus className="w-4 h-4" />{addLabel}</button>
  </div>
);

export const ProductEditor = ({ product, onClose, onSaved }) => {
  const [f, setF] = useState({ ...EMPTY, ...product, sale_price: product.sale_price ?? "", colours: product.colours || [] });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    setBusy(true);
    const body = { ...f, price: Number(f.price), sale_price: f.sale_price === "" ? null : Number(f.sale_price), stock: Number(f.stock) || 0 };
    try {
      if (product.id) await api.put(`/admin/products/${product.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Product saved");
      await onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const t = (k, label, props = {}) => <Field label={label}><input className="rh-input" value={f[k] ?? ""} onChange={set(k)} data-testid={`product-field-${k}`} {...props} /></Field>;
  const ta = (k, label, rows = 3) => <Field label={label}><textarea rows={rows} className="rh-input" value={f[k] ?? ""} onChange={set(k)} data-testid={`product-field-${k}`} /></Field>;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto rounded-[22px] bg-pearl p-6 sm:p-8" data-testid="product-editor">
        <DialogTitle className="font-display text-[32px]">{product.id ? "Edit product" : "Add product"}</DialogTitle>
        <div className="space-y-6">
          <ImagesField value={f.images} onChange={set("images")} />
          <div className="grid sm:grid-cols-2 gap-4">
            {t("name", "Product name")}
            {t("type", "Type (e.g. Necklace)")}
            {t("price", "Price (₹)", { type: "number", min: 1 })}
            {t("sale_price", "Sale price (₹) — optional", { type: "number", min: 0 })}
            {t("label", "Small label (e.g. Flagship)")}
            {t("tagline", "One-line description")}
          </div>
          {ta("description", "Description", 4)}
          {ta("story", "Short story (used in Hero Product Story)", 2)}
          <div className="rounded-[16px] bg-white border border-plum/10 p-4 space-y-3">
            <p className="font-bold">Variants & stock</p>
            <p className="text-[13px] text-plum/60">Add sizes or lengths with their own stock. Leave empty to use a single stock number.</p>
            {t("variant_name", "Variant name (e.g. Ring size)")}
            <Rows rows={f.variants} onChange={set("variants")} a={{ k: "label", ph: "e.g. Size 8" }} b={{ k: "stock", ph: "Stock" }} bType="number" addLabel="Add variant" testId="variant-row" />
            {f.variants.length === 0 && t("stock", "Stock", { type: "number", min: 0 })}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Colours (comma separated)"><input className="rh-input" value={f.colours.join(", ")} onChange={(e) => setF({ ...f, colours: e.target.value.split(",").map((s) => s.trimStart()).filter((s, i, a) => s || i === a.length - 1) })} onBlur={() => setF({ ...f, colours: f.colours.map((c) => c.trim()).filter(Boolean) })} data-testid="product-field-colours" /></Field>
            {t("materials", "Materials (only if known)")}
          </div>
          <div>
            <p className="text-[14px] font-bold mb-2">Specifications</p>
            <Rows rows={f.specs} onChange={set("specs")} a={{ k: "label", ph: "e.g. Closure" }} b={{ k: "value", ph: "e.g. Push back" }} addLabel="Add specification" testId="spec-row" />
          </div>
          <div>
            <p className="text-[14px] font-bold mb-2">Occasions (for "What Is Your Occasion?")</p>
            <div className="flex flex-wrap gap-2">
              {OCC.map(([k, l]) => {
                const on = f.occasions.includes(k);
                return <button type="button" key={k} onClick={() => setF({ ...f, occasions: on ? f.occasions.filter((x) => x !== k) : [...f.occasions, k] })} className={`h-10 px-4 rounded-[12px] text-[13px] font-bold border ${on ? "bg-plum text-pearl border-plum" : "border-plum/20 bg-white"}`} data-testid={`occasion-toggle-${k}`}>{l}</button>;
              })}
            </div>
          </div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 font-bold text-[14px]"><Switch checked={f.published} onCheckedChange={set("published")} />Published</label>
            <label className="flex items-center gap-2 font-bold text-[14px]"><Switch checked={f.featured} onCheckedChange={set("featured")} />Featured</label>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-pearl">
          <Btn variant="light" onClick={onClose}>Cancel</Btn>
          <Btn variant="berry" onClick={save} disabled={busy} data-testid="product-save-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Save product</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
};
